import React, { useState, useEffect } from 'react';
import './DailyNews.css';

const DailyNews = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [manualPosts, setManualPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [editingPost, setEditingPost] = useState(null);

  // Load posts from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('manualPosts');
    if (saved) {
      setManualPosts(JSON.parse(saved));
    } else {
      // Default posts if none exist
      const defaultPosts = [
        {
          id: 1,
          title: 'AICIS 2025 Summit a Success',
          excerpt: 'The AICIS 2025 summit in Abuja brought together global leaders to address climate change and infrastructure development. The event featured high-level discussions on sustainable investment and climate resilience.',
          date: 'August 15, 2025',
          source: 'ABVIG Official'
        },
        {
          id: 2,
          title: 'Zenith Bank Reaffirms Support for AICIS 2026',
          excerpt: 'Zenith Bank has committed to supporting the AICIS 2026 Global Summit scheduled for November 25-28, 2026. The bank will serve as a key sponsor for the event.',
          date: 'October 24, 2025',
          source: 'ABVIG Official'
        },
        {
          id: 3,
          title: 'SGF Harps on Collective Actions in Mitigating Climate Change',
          excerpt: 'Secretary to the Government of the Federation called for stakeholders actions in mitigating the impacts of climate change at AICIS 2025. The SGF received an award from ABVIG for promoting climate change initiatives.',
          date: 'August 12, 2025',
          source: 'ABVIG Official'
        }
      ];
      localStorage.setItem('manualPosts', JSON.stringify(defaultPosts));
      setManualPosts(defaultPosts);
    }
  }, []);

  // Admin Login
  const handleLogin = (e) => {
    e.preventDefault();
    // Change this password to whatever you want
    if (password === 'abvig2026') {
      setIsAdmin(true);
      setError('');
      setPassword('');
    } else {
      setError('Incorrect password. Try again.');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setShowForm(false);
    setEditingPost(null);
  };

  // Add or Edit Post
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      setError('Please fill in both fields.');
      return;
    }

    let updatedPosts;
    if (editingPost) {
      // Edit existing post
      updatedPosts = manualPosts.map(post => 
        post.id === editingPost.id 
          ? { ...post, title, excerpt: content, date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) }
          : post
      );
    } else {
      // Add new post
      const newPost = {
        id: Date.now(),
        title,
        excerpt: content,
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        source: 'ABVIG Official'
      };
      updatedPosts = [newPost, ...manualPosts];
    }

    localStorage.setItem('manualPosts', JSON.stringify(updatedPosts));
    setManualPosts(updatedPosts);
    setTitle('');
    setContent('');
    setShowForm(false);
    setEditingPost(null);
    setError('');
  };

  // Delete Post
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this news post?')) {
      const updatedPosts = manualPosts.filter(post => post.id !== id);
      localStorage.setItem('manualPosts', JSON.stringify(updatedPosts));
      setManualPosts(updatedPosts);
    }
  };

  // Edit Post
  const handleEdit = (post) => {
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.excerpt);
    setShowForm(true);
  };

  return (
    <div>
      <section className="page-hero">
        <h1>Daily News</h1>
        <p>Latest updates and announcements from AICIS</p>
      </section>

      <section className="news-page-section">
        <div className="container">
          {/* Admin Panel */}
          <div className="admin-panel">
            {!isAdmin ? (
              <div className="admin-login">
                <h3>Admin Login</h3>
                <form onSubmit={handleLogin}>
                  <input 
                    type="password" 
                    placeholder="Enter admin password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type="submit" className="btn-gold">Login</button>
                  {error && <p className="error-text">{error}</p>}
                </form>
              </div>
            ) : (
              <div className="admin-controls">
                <div className="admin-buttons">
                  <button 
                    className="btn-gold" 
                    onClick={() => {
                      setShowForm(!showForm);
                      setEditingPost(null);
                      setTitle('');
                      setContent('');
                      setError('');
                    }}
                  >
                    {showForm ? 'Cancel' : '+ Post New News'}
                  </button>
                  <button className="btn-outline-gold" onClick={handleLogout}>Logout</button>
                </div>
                
                {showForm && (
                  <form className="post-news-form" onSubmit={handleSubmit}>
                    <h3>{editingPost ? 'Edit News' : 'Post New News'}</h3>
                    <input 
                      type="text" 
                      placeholder="News Title" 
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                    <textarea 
                      placeholder="News Content" 
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows="4"
                      required
                    />
                    {error && <p className="error-text">{error}</p>}
                    <button type="submit" className="btn-gold">
                      {editingPost ? 'Update News' : 'Publish News'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* News Grid */}
          <div className="news-section">
            <h2 className="section-title">Latest News</h2>
            {manualPosts.length === 0 ? (
              <p className="no-news">No news posts available. Click "Post New News" to add one.</p>
            ) : (
              <div className="news-grid">
                {manualPosts.map((post) => (
                  <div className="news-card" key={post.id}>
                    <div className="news-content">
                      <span className="news-category official">Official</span>
                      <h3>{post.title}</h3>
                      <p className="news-excerpt">{post.excerpt}</p>
                      <div className="news-footer">
                        <p className="news-date">{post.date}</p>
                        <p className="news-source">{post.source}</p>
                      </div>
                      {isAdmin && (
                        <div className="admin-actions">
                          <button className="edit-btn" onClick={() => handleEdit(post)}>✎ Edit</button>
                          <button className="delete-btn" onClick={() => handleDelete(post.id)}>✕ Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DailyNews;